"use client";

import { Children, useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import type { PbSliderSettings } from "../types";
import { attrsToProps, cx } from "../render/attrs";

type Props = {
  attrs: Record<string, string>;
  settings: PbSliderSettings;
  labels?: { previous: string; next: string; slide: string; carousel: string };
  children?: ReactNode;
};

/** `inert` is not in React 18's DOM typings; an empty string renders the boolean attribute. */
function inertUnless(active: boolean): Record<string, string> {
  return active ? {} : { inert: "" };
}

const DEFAULT_LABELS = { previous: "Previous", next: "Next", slide: "Slide", carousel: "Carousel" };

/**
 * Page Builder Slider (Magento: slick 1.9.0) as a dependency-free carousel.
 *
 * Slide mode is a CSS scroll-snap track: the server HTML already shows the
 * first slide (no hidden-until-init flash, no cloned slides, native touch
 * swipe), JS only adds arrows/dots/autoplay. Fade mode stacks slides in one
 * grid cell and cross-fades (500 ms, slick default). Option parity:
 * `autoplay`, `autoplay-speed`, `fade`, `infinite-loop`, `show-arrows`,
 * `show-dots`; slick defaults: pause on hover / focus, keyboard arrows.
 */
export function PbSlider({ attrs, settings, labels = DEFAULT_LABELS, children }: Props) {
  const slides = Children.toArray(children);
  const count = slides.length;
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const activeRef = useRef(0);
  activeRef.current = active;

  const goTo = useCallback(
    (target: number, behavior: ScrollBehavior = "smooth") => {
      if (count === 0) return;
      let index = target;
      if (settings.infinite) index = ((target % count) + count) % count;
      else index = Math.min(count - 1, Math.max(0, target));
      setActive(index);
      if (settings.fade) return;
      const track = trackRef.current;
      const slide = track?.children[index] as HTMLElement | undefined;
      if (track && slide) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior });
    },
    [count, settings.fade, settings.infinite]
  );

  // Track the visible slide while the user swipes / scrolls the track.
  useEffect(() => {
    const track = trackRef.current;
    if (!track || settings.fade) return;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const width = track.clientWidth || 1;
        const index = Math.round(track.scrollLeft / width);
        if (index !== activeRef.current) setActive(Math.min(count - 1, Math.max(0, index)));
      });
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      track.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [count, settings.fade]);

  // Keep the current slide aligned whenever the track changes size.
  useEffect(() => {
    const track = trackRef.current;
    if (!track || settings.fade) return;
    let width = track.clientWidth;
    const ro = new ResizeObserver(() => {
      if (track.clientWidth === width) return;
      width = track.clientWidth;
      goTo(activeRef.current, "auto");
    });
    ro.observe(track);
    return () => ro.disconnect();
  }, [goTo, settings.fade]);

  // Autoplay (paused on hover/focus, hidden tab and reduced motion).
  useEffect(() => {
    if (!settings.autoplay || paused || count < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      const next = activeRef.current + 1;
      if (!settings.infinite && next >= count) return;
      goTo(next);
    }, Math.max(1000, settings.autoplaySpeed));
    return () => window.clearInterval(timer);
  }, [settings.autoplay, settings.autoplaySpeed, settings.infinite, paused, count, goTo]);

  // Fade mode has no scroll track: support horizontal swipe.
  const swipeStart = useRef<number | null>(null);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      goTo(activeRef.current - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      goTo(activeRef.current + 1);
    }
  };

  const atStart = !settings.infinite && active === 0;
  const atEnd = !settings.infinite && active === count - 1;
  const mainProps = attrsToProps(attrs, cx("pb-slider", settings.fade && "pb-slider--fade"));

  return (
    <div
      {...mainProps}
      role="region"
      aria-roledescription={labels.carousel}
      data-pb-initialized=""
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={onKeyDown}
    >
      <div
        ref={trackRef}
        className="pb-slider__track"
        aria-live={settings.autoplay && !paused ? "off" : "polite"}
        onPointerDown={settings.fade ? (e) => (swipeStart.current = e.clientX) : undefined}
        onPointerUp={
          settings.fade
            ? (e) => {
                if (swipeStart.current === null) return;
                const dx = e.clientX - swipeStart.current;
                swipeStart.current = null;
                if (Math.abs(dx) > 40) goTo(activeRef.current + (dx < 0 ? 1 : -1));
              }
            : undefined
        }
      >
        {slides.map((slide, i) => (
          <div
            key={i}
            className={cx("pb-slider__slide", i === active && "is-active")}
            role="group"
            aria-roledescription={labels.slide}
            aria-label={`${i + 1} / ${count}`}
            aria-hidden={i === active ? undefined : true}
            {...inertUnless(i === active)}
          >
            {slide}
          </div>
        ))}
      </div>

      {settings.showArrows && count > 1 && (
        <>
          <button
            type="button"
            className="pb-slider__arrow pb-slider__arrow--prev"
            aria-label={labels.previous}
            disabled={atStart}
            onClick={() => goTo(active - 1)}
          />
          <button
            type="button"
            className="pb-slider__arrow pb-slider__arrow--next"
            aria-label={labels.next}
            disabled={atEnd}
            onClick={() => goTo(active + 1)}
          />
        </>
      )}

      {settings.showDots && count > 1 && (
        <ul className="pb-slider__dots">
          {slides.map((_, i) => (
            <li key={i} className={i === active ? "is-active" : undefined}>
              <button
                type="button"
                aria-label={`${labels.slide} ${i + 1}`}
                aria-current={i === active ? "true" : undefined}
                onClick={() => goTo(i)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
